import { useCallback, useEffect, useMemo, useState } from 'react'
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, Database, ToggleLeft, ToggleRight } from 'lucide-react'
import { getModuleLabel, useDemoDataSettings, type DemoDataSettings } from '@/context/DemoDataContext'
import { WORKFLOW_ORDER } from '@/config/workflowModules'
import type { PlatformModuleId } from '@/types'
import api from '@/services/api'
import BulkRetirePanel from '@/components/settings/BulkRetirePanel'

/**
 * What an operator types to archive every deal (C31/C30). A phrase, not a word
 * or a number: the bulk "mark Dead" panel above asks for the deal count, and
 * "RESET" was one slip away from the wrong control.
 */
const RESET_CONFIRM_PHRASE = 'ARCHIVE ALL DEALS'

function SectionHeading({ children }: { children: React.ReactNode }) {
  return <h2 className="text-[13px] font-semibold uppercase tracking-[0.14em] text-stone-300">{children}</h2>
}

/** GET/PUT /admin/test-data-reset — whether the whole-board reset may run. */
interface ResetSwitchState {
  enabled: boolean
  switchOn: boolean
  /** ALLOW_TEST_DATA_RESET=true on the server: on, and not switchable from here. */
  environmentOverride: boolean
  changedBy: string | null
  changedAt: string | null
}

function errorDetail(err: unknown, fallback: string): string {
  return (
    (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
    (err instanceof Error ? err.message : fallback)
  )
}

interface DemoDataResponse {
  data: { masterEnabled?: boolean; modules?: Record<string, boolean> }
  storedMasterEnabled?: boolean
  environment?: string
  activePartition?: 'demo' | 'live'
}

function storedFromResponse(res: DemoDataResponse): DemoDataSettings {
  const modules = Object.fromEntries(
    WORKFLOW_ORDER.map((id) => [id, Boolean(res.data?.modules?.[id])]),
  ) as Record<string, boolean>
  return {
    // The tab edits the STORED platform configuration. `data.masterEnabled` is
    // the effective value for the caller's account (forced off outside the
    // demo environment), so the master switch must render from the stored flag
    // or it appears to silently revert after every save.
    masterEnabled: Boolean(res.storedMasterEnabled ?? res.data?.masterEnabled),
    modules,
  }
}

export default function DemoDataSettingsTab() {
  const { refresh, canManageDemoSettings } = useDemoDataSettings()
  const [stored, setStored] = useState<DemoDataSettings | null>(null)
  const [environment, setEnvironment] = useState<string>('live')
  const [activePartition, setActivePartition] = useState<'demo' | 'live'>('live')
  const [loading, setLoading] = useState(true)
  const [draft, setDraft] = useState<DemoDataSettings | null>(null)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [resetOpen, setResetOpen] = useState(false)
  const [resetConfirmText, setResetConfirmText] = useState('')
  const [resetting, setResetting] = useState(false)
  const [resetResult, setResetResult] = useState<{ archivedLoans: number; archivedIngestions: number } | null>(null)
  const [resetError, setResetError] = useState<string | null>(null)
  const [resetSwitch, setResetSwitch] = useState<ResetSwitchState | null>(null)
  const [resetSwitchSaving, setResetSwitchSaving] = useState(false)
  const [resetSwitchError, setResetSwitchError] = useState<string | null>(null)

  // Loaded on its own so a failure here never blanks the demo switches above.
  useEffect(() => {
    if (!canManageDemoSettings) return
    let active = true
    void (async () => {
      try {
        const res = await api.get<ResetSwitchState>('/admin/test-data-reset')
        if (active) setResetSwitch(res.data)
      } catch (err) {
        if (active) setResetSwitchError(errorDetail(err, 'Could not read the reset switch'))
      }
    })()
    return () => {
      active = false
    }
  }, [canManageDemoSettings])

  const handleResetSwitch = useCallback(async (enabled: boolean) => {
    setResetSwitchSaving(true)
    setResetSwitchError(null)
    try {
      const res = await api.put<ResetSwitchState>('/admin/test-data-reset', { enabled })
      setResetSwitch(res.data)
      if (!res.data.enabled) setResetConfirmText('')
    } catch (err) {
      setResetSwitchError(errorDetail(err, 'Could not change the reset switch'))
    } finally {
      setResetSwitchSaving(false)
    }
  }, [])

  useEffect(() => {
    if (!canManageDemoSettings) return
    let active = true
    void (async () => {
      try {
        const res = await api.get<DemoDataResponse>('/settings/demo-data')
        if (!active) return
        setStored(storedFromResponse(res.data))
        setEnvironment(res.data.environment ?? 'live')
        setActivePartition(res.data.activePartition === 'demo' ? 'demo' : 'live')
      } catch (err) {
        if (!active) return
        setError(err instanceof Error ? err.message : 'Could not load demo data settings')
      } finally {
        if (active) setLoading(false)
      }
    })()
    return () => {
      active = false
    }
  }, [canManageDemoSettings])

  const settings: DemoDataSettings = stored ?? {
    masterEnabled: false,
    modules: Object.fromEntries(WORKFLOW_ORDER.map((id) => [id, false])) as Record<string, boolean>,
  }
  const current = draft ?? settings

  const handleResetTestData = useCallback(async () => {
    if (!resetSwitch?.enabled || resetConfirmText !== RESET_CONFIRM_PHRASE) return
    setResetting(true)
    setResetError(null)
    setResetResult(null)
    try {
      const res = await api.post<{ archived_loans: number; archived_ingestions: number }>(
        '/admin/reset-test-data',
        { confirm: 'RESET' },
      )
      setResetResult({ archivedLoans: res.data.archived_loans, archivedIngestions: res.data.archived_ingestions })
      setResetConfirmText('')
    } catch (err) {
      setResetError(errorDetail(err, 'Could not reset test data'))
    } finally {
      setResetting(false)
    }
  }, [resetConfirmText, resetSwitch])

  const enabledModuleCount = useMemo(
    () => WORKFLOW_ORDER.filter((id) => current.modules[id]).length,
    [current.modules],
  )

  const setMaster = useCallback((masterEnabled: boolean) => {
    setDraft((prev) => ({ ...(prev ?? settings), masterEnabled }))
  }, [settings])

  const setModule = useCallback((moduleId: PlatformModuleId, enabled: boolean) => {
    setDraft((prev) => {
      const base = prev ?? settings
      return {
        ...base,
        modules: { ...base.modules, [moduleId]: enabled },
      }
    })
  }, [settings])

  const setAllModules = useCallback((enabled: boolean) => {
    setDraft((prev) => {
      const base = prev ?? settings
      const modules = Object.fromEntries(WORKFLOW_ORDER.map((id) => [id, enabled])) as Record<string, boolean>
      return { ...base, modules }
    })
  }, [settings])

  const handleSave = useCallback(async () => {
    if (!canManageDemoSettings) return
    setSaving(true)
    setError(null)
    setSaved(false)
    try {
      const res = await api.patch<{ data: Record<string, unknown> }>('/settings/demo-data', {
        masterEnabled: current.masterEnabled,
        modules: current.modules,
      })
      // PATCH echoes the STORED configuration — render exactly what persisted.
      setStored(storedFromResponse({ data: res.data.data as DemoDataResponse['data'] }))
      setDraft(null)
      setSaved(true)
      // Sync app-wide rendering (no-op visually for non-demo accounts, which
      // never render demo content regardless of the stored configuration).
      void refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save demo data settings')
    } finally {
      setSaving(false)
    }
  }, [canManageDemoSettings, current, refresh])

  if (!canManageDemoSettings) {
    return (
      <div className="text-[12px] text-stone-500">
        Demo data controls are restricted to the permanent super admin.
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-amber-400/15 bg-amber-400/[0.04] p-4">
        <div className="flex items-start gap-3">
          <Database size={18} className="mt-0.5 text-amber-300" />
          <div>
            <div className="text-[13px] font-medium text-stone-100">
              Trial / onboarding dataset — not production live data
            </div>
            <p className="mt-1 text-[11px] leading-relaxed text-stone-400">
              This tab only governs the illustrative sample portfolio for{' '}
              <span className="font-mono text-amber-200/90">demo</span>-environment trial accounts.
              Live and calibration accounts are not shown that dataset, regardless of these switches:
              a screen that carries sample figures renders them only for a demo account, and otherwise
              shows live data or names the source that is missing. A build check fails if a screen
              declares sample figures without consulting this switch. Turning a switch on here does not
              invent figures on Integrations, CRM, or other settings surfaces.
            </p>
          </div>
        </div>
      </div>

      {environment !== 'demo' ? (
        <div className="rounded-lg border border-amber-400/20 bg-amber-400/[0.06] p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle size={16} className="mt-0.5 text-amber-300" />
            <p className="text-[11px] leading-relaxed text-stone-300">
              Your account is in the <span className="font-mono text-amber-200/90">{environment}</span>{' '}
              environment, so the sample portfolio is not shown to it — regardless of these switches. Your
              account&apos;s partition: <span className="font-mono text-amber-200/90">{activePartition}</span>.
              To present with demo data, sign in with a demo-environment account (Settings → User
              Management → Environment).
            </p>
          </div>
        </div>
      ) : null}

      {error ? <div className="text-[11px] text-red-300">{error}</div> : null}
      {loading ? <div className="text-[12px] text-stone-500">Loading demo data settings…</div> : null}

      <SectionHeading>Master demo data switch</SectionHeading>
      <div className="flex items-center justify-between rounded-lg border border-amber-400/10 bg-black/30 px-4 py-3">
        <div>
          <div className="text-[12px] font-medium text-stone-100">
            {current.masterEnabled ? 'Demo data enabled' : 'Demo accounts see a blank canvas'}
          </div>
          <div className="text-[11px] text-stone-500">
            Stored platform-wide; applies to demo-environment accounts. Your account&apos;s partition:{' '}
            <span className="font-mono text-stone-300">{activePartition}</span>
          </div>
        </div>
        <button
          type="button"
          aria-label="Toggle master demo data"
          onClick={() => setMaster(!current.masterEnabled)}
          className="text-amber-300"
        >
          {current.masterEnabled ? <ToggleRight size={32} /> : <ToggleLeft size={32} className="text-stone-600" />}
        </button>
      </div>

      <div className="flex items-center justify-between">
        <SectionHeading>Module demo toggles</SectionHeading>
        <div className="flex gap-2">
          <button type="button" className="btn-secondary text-[10px] px-2 py-1" onClick={() => setAllModules(true)}>
            Enable all
          </button>
          <button type="button" className="btn-secondary text-[10px] px-2 py-1" onClick={() => setAllModules(false)}>
            Disable all
          </button>
        </div>
      </div>

      <div className="text-[10px] text-stone-500">
        {enabledModuleCount} of {WORKFLOW_ORDER.length} modules will show demo content when master demo mode is on.
      </div>

      <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
        {WORKFLOW_ORDER.map((moduleId) => {
          const enabled = Boolean(current.modules[moduleId])
          return (
            <div
              key={moduleId}
              className="flex items-center justify-between rounded-lg border border-amber-400/10 bg-black/20 px-3 py-2.5"
            >
              <div>
                <div className="text-[12px] text-stone-100">{getModuleLabel(moduleId)}</div>
                <div className="font-mono text-[9px] uppercase tracking-wider text-stone-600">{moduleId}</div>
              </div>
              <button
                type="button"
                aria-label={`Toggle demo data for ${moduleId}`}
                disabled={!current.masterEnabled}
                onClick={() => setModule(moduleId, !enabled)}
                className={current.masterEnabled ? 'text-amber-300' : 'text-stone-700'}
              >
                {enabled && current.masterEnabled ? (
                  <ToggleRight size={26} />
                ) : (
                  <ToggleLeft size={26} className="text-stone-600" />
                )}
              </button>
            </div>
          )
        })}
      </div>

      <div className="flex items-center gap-3 pt-2">
        <button
          type="button"
          className="btn-primary text-[12px] disabled:opacity-50"
          disabled={loading || saving}
          onClick={() => void handleSave()}
        >
          {saving ? 'Saving…' : 'Save demo data settings'}
        </button>
        {saved ? (
          <span className="flex items-center gap-1 text-[11px] text-emerald-400">
            <CheckCircle2 size={12} /> Saved
          </span>
        ) : null}
      </div>

      <SectionHeading>Retire the current pipeline</SectionHeading>
      <BulkRetirePanel />

      {/* Set apart from the retire panel above (C31/C30): its own danger zone
          at the foot of the tab, closed until opened, with its own phrase to
          type, so the parallel-run "mark Dead" flow cannot slide into
          archiving the whole book. */}
      <section aria-labelledby="demo-data-danger-zone" className="mt-10 border-t border-red-400/25 pt-6">
        <div className="flex items-center gap-3">
          <h2 id="demo-data-danger-zone" className="text-[13px] font-semibold uppercase tracking-[0.14em] text-red-300">
            Danger zone
          </h2>
          {/* Visible with the panel closed, so a reset left switched on is not forgotten. */}
          {resetSwitch?.enabled ? (
            <span className="rounded border border-red-400/40 bg-red-400/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-red-200">
              Reset switched on
            </span>
          ) : null}
        </div>
        <button
          type="button"
          aria-expanded={resetOpen}
          onClick={() => setResetOpen((open) => !open)}
          className="mt-3 flex items-center gap-1.5 text-[12px] text-stone-300 hover:text-stone-100"
        >
          {resetOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          {resetOpen ? 'Hide reset test data' : 'Show reset test data (archives every deal)'}
        </button>
        {resetOpen ? (
          <div className="mt-3 space-y-3 rounded-lg border border-red-400/30 bg-red-400/[0.05] p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle size={18} className="mt-0.5 text-red-300" />
              <div>
                <div className="text-[13px] font-medium text-stone-100">
                  Reset test data: archive every loan and intake record
                </div>
                <p className="mt-1 text-[11px] leading-relaxed text-stone-400">
                  Clears the pipeline and intake queue to a blank state for a live test run. Rows are{' '}
                  <span className="text-stone-200">archived, never deleted</span> — they disappear from every list,
                  queue and export, the Deal Locator included, but remain in the database and are recoverable by
                  direct lookup.
                </p>
                <p className="mt-1 text-[11px] leading-relaxed text-red-200/90">
                  This is not the same as marking deals Dead above. Dead deals stay findable with their kill reason;
                  archived deals leave every screen.
                </p>
              </div>
            </div>
            <div className="flex items-center justify-between gap-4 rounded border border-red-400/20 bg-black/30 px-3 py-2.5">
              <div>
                <div className="text-[12px] font-medium text-stone-100">
                  {resetSwitch === null
                    ? 'Reset switch'
                    : resetSwitch.enabled
                      ? 'Reset switch: on — the archive below will run'
                      : 'Reset switch: off — the archive below is refused'}
                </div>
                <div className="text-[11px] text-stone-500">
                  {resetSwitch?.environmentOverride
                    ? 'Held on by the server (ALLOW_TEST_DATA_RESET=true). Turn it off there, not here.'
                    : resetSwitch?.changedBy && resetSwitch.changedAt
                      ? `Last switched ${resetSwitch.switchOn ? 'on' : 'off'} by ${resetSwitch.changedBy}, ${new Date(resetSwitch.changedAt).toLocaleString()}. Stays as set until switched back.`
                      : 'Development stage only. Switch it on for a test reset, then back off.'}
                </div>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={Boolean(resetSwitch?.enabled)}
                aria-label="Allow test-data reset"
                disabled={resetSwitch === null || resetSwitch.environmentOverride || resetSwitchSaving}
                onClick={() => resetSwitch && void handleResetSwitch(!resetSwitch.switchOn)}
                className="text-red-300 disabled:opacity-40"
              >
                {resetSwitch?.enabled ? <ToggleRight size={32} /> : <ToggleLeft size={32} className="text-stone-600" />}
              </button>
            </div>
            {resetSwitchError ? <div className="text-[11px] text-red-300">{resetSwitchError}</div> : null}
            {resetError ? <div className="text-[11px] text-red-300">{resetError}</div> : null}
            {resetResult ? (
              <div className="flex items-center gap-1 text-[11px] text-emerald-400">
                <CheckCircle2 size={12} /> Archived {resetResult.archivedLoans} loan(s) and {resetResult.archivedIngestions}{' '}
                intake record(s).
              </div>
            ) : null}
            <p className="text-[11px] text-stone-400">
              Type <span className="font-mono text-red-200">{RESET_CONFIRM_PHRASE}</span> to confirm.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="text"
                aria-label="Confirmation phrase"
                autoComplete="off"
                spellCheck={false}
                value={resetConfirmText}
                onChange={(e) => setResetConfirmText(e.target.value)}
                placeholder={RESET_CONFIRM_PHRASE}
                className="w-56 rounded border border-red-400/20 bg-black/40 px-3 py-2 font-mono text-[12px] text-stone-100 placeholder:text-stone-700 focus:border-red-400/40 focus:outline-none"
              />
              <button
                type="button"
                className="rounded border border-red-400/30 bg-red-400/10 px-3 py-2 text-[12px] font-medium text-red-300 disabled:opacity-40"
                disabled={!resetSwitch?.enabled || resetConfirmText !== RESET_CONFIRM_PHRASE || resetting}
                onClick={() => void handleResetTestData()}
              >
                {resetting ? 'Archiving…' : 'Archive every deal and intake record'}
              </button>
            </div>
          </div>
        ) : null}
      </section>
    </div>
  )
}
